import axios from 'axios';
import authHeader from './auth-header';
import config from '../config';

// Use the config if available, otherwise fallback to the hardcoded URL
const API_URL = config.API_URL;

class AdminService {
  constructor() {
    // Cache pour éviter les requêtes multiples
    this._statsCache = null;
    this._statsCacheTime = 0;
    this.CACHE_DURATION = 30000; // 30 secondes
  }

  // Construire une liste d'activités récentes à partir des données existantes
  async getRecentActivities() {
    try {
      const [users, matches] = await Promise.all([
        this.getAllUsers(),
        this.getAllMatches()
      ]);

      const activities = [];

      // Activités liées aux matchs
      matches.forEach(m => {
        activities.push({
          type: 'match',
          title: 'Nouveau match enregistré',
          message: `${m.player1} vs ${m.player2} — Score ${m.score}`,
          timestamp: m.date || new Date().toISOString()
        });

        if (m.statut && String(m.statut).toUpperCase() === 'VALIDATED') {
          activities.push({
            type: 'moderation',
            title: 'Match validé',
            message: `Le match ${m.player1} vs ${m.player2} a été validé`,
            timestamp: m.date || new Date().toISOString()
          });
        }
      });

      // Activités liées aux utilisateurs: inscriptions (faute de createdAt côté DTO, on liste les utilisateurs)
      users.forEach(u => {
        activities.push({
          type: 'user',
          title: 'Nouvel utilisateur',
          message: `${u.username} (${u.email}) s'est inscrit sur la plateforme.`,
          timestamp: new Date().toISOString()
        });
      });

      // Tri décroissant par date
      activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      // Limiter à 20 dernières activités
      return activities.slice(0, 20);
    } catch (error) {
      console.error('Erreur lors de la récupération des activités récentes:', error);
      // Fallback sur les activités mockées
      return this.generateMockActivities();
    }
  }

  // Récupérer les statistiques générales avec cache
  async getStats() {
    const now = Date.now();
    
    // Retourner le cache si il est encore valide
    if (this._statsCache && (now - this._statsCacheTime) < this.CACHE_DURATION) {
      return this._statsCache;
    }

    try {
      // Récupérer les utilisateurs et matchs pour calculer les stats
      const [usersResponse, matchesResponse] = await Promise.all([
        axios.get(`${API_URL}/users`, { headers: authHeader() }),
        axios.get(`${API_URL}/matches`, { headers: authHeader() })
      ]);

      const users = usersResponse.data;
      const matches = matchesResponse.data;

      // Calculer les statistiques
      const nowDate = new Date();
      const thisMonth = new Date(nowDate.getFullYear(), nowDate.getMonth(), 1);
      
      const monthlyMatches = matches.filter(match => {
        const matchDate = new Date(match.date);
        return matchDate >= thisMonth;
      }).length;

      const activeUsers = users.filter(user => user.matchesPlayed > 0).length;
      const activePlayersPercentage = users.length > 0 ? Math.round((activeUsers / users.length) * 100) : 0;

      this._statsCache = {
        users: users.length,
        newUsers: 0, // À implémenter avec un système de tracking des nouveaux utilisateurs
        matches: matches.length,
        newMatches: 0, // À implémenter avec un système de tracking des nouveaux matchs
        activePlayers: activePlayersPercentage,
        monthlyMatches: monthlyMatches
      };
      
      this._statsCacheTime = now;
      return this._statsCache;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      throw error;
    }
  }

  // Récupérer tous les utilisateurs avec leurs statistiques
  async getAllUsers() {
    try {
      const response = await axios.get(`${API_URL}/users`, { headers: authHeader() });
      console.log('Données utilisateurs reçues:', response.data);
      
      return response.data.map(user => ({
        id: user.id,
        username: user.username,
        email: user.email,
        roles: user.roles || [],
        active: true, // Par défaut, tous les utilisateurs sont actifs (pas de champ active dans UserDTO)
        matchCount: user.matchesPlayed || 0,
        elo: user.elo || 1000,
        wins: user.wins || 0,
        losses: user.losses || 0
      }));
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs:', error);
      throw error;
    }
  }

  // Récupérer tous les matchs
  async getAllMatches() {
    try {
      const response = await axios.get(`${API_URL}/matches`, { headers: authHeader() });
      console.log('Données matchs reçues:', response.data);
      
      return response.data.map(match => ({
        id: match.id,
        player1: match.user1?.username || 'Inconnu',
        player2: match.user2?.username || 'Inconnu',
        score: `${match.scoreUser1 || 0} - ${match.scoreUser2 || 0}`,
        winner: match.user1?.username || 'Inconnu', // Simplification pour l'affichage
        armee1: match.armee1 || 'Non spécifiée',
        armee2: match.armee2 || 'Non spécifiée',
        date: match.date,
        time: match.time,
        statut: match.statut,
        matchType: match.matchType || 'LADDER', // Récupérer le type de match depuis l'API
        eloBefore1: match.eloBefore1,
        eloBefore2: match.eloBefore2,
        eloAfter1: match.eloAfter1,
        eloAfter2: match.eloAfter2
      }));
    } catch (error) {
      console.error('Erreur lors de la récupération des matchs:', error);
      throw error;
    }
  }

  // Mettre à jour un utilisateur
  async updateUser(userId, userData) {
    try {
      // Préparer les données pour le backend (enlever les champs qui ne sont pas dans UserDTO)
      const updateData = {
        username: userData.username,
        email: userData.email
        // Note: active n'est pas géré par le backend pour l'instant
        // Les rôles ne sont pas modifiables via cette interface
      };
      
      console.log('Mise à jour utilisateur:', { userId, updateData });
      
      const response = await axios.put(`${API_URL}/users/${userId}`, updateData, { headers: authHeader() });
      this.invalidateStatsCache(); // Invalider le cache après modification
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'utilisateur:', error);
      throw error;
    }
  }

  // Supprimer un utilisateur
  async deleteUser(userId) {
    try {
      const response = await axios.delete(`${API_URL}/users/${userId}`, { headers: authHeader() });
      this.invalidateStatsCache(); // Invalider le cache après suppression
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'utilisateur:', error);
      throw error;
    }
  }

  // Mettre à jour un match
  async updateMatch(matchId, matchData) {
    try {
      // Préparer les données pour le backend
      const updateData = {
        user1Id: matchData.player1, // Assumer que player1 contient l'ID
        user2Id: matchData.player2, // Assumer que player2 contient l'ID
        scoreUser1: matchData.scoreUser1,
        scoreUser2: matchData.scoreUser2,
        armee1: matchData.armee1,
        armee2: matchData.armee2,
        date: matchData.date,
        matchType: matchData.matchType
      };
      
      console.log('Mise à jour match:', { matchId, updateData });
      
      const response = await axios.put(`${API_URL}/matches/${matchId}`, updateData, { headers: authHeader() });
      this.invalidateStatsCache(); // Invalider le cache après modification
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du match:', error);
      throw error;
    }
  }

  // Supprimer un match
  async deleteMatch(matchId) {
    try {
      const response = await axios.delete(`${API_URL}/matches/${matchId}`, { headers: authHeader() });
      this.invalidateStatsCache(); // Invalider le cache après suppression
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression du match:', error);
      throw error;
    }
  }


  // Invalider le cache des statistiques
  invalidateStatsCache() {
    this._statsCache = null;
    this._statsCacheTime = 0;
  }

  // Générer des activités récentes (pour l'instant mockées)
  generateMockActivities() {
    return [
      {
        type: 'match',
        title: 'Nouveau match enregistré',
        message: 'Un match a été ajouté au système.',
        timestamp: new Date(Date.now() - 3600000).toISOString()
      },
      {
        type: 'user',
        title: 'Nouvel utilisateur',
        message: 'Un nouvel utilisateur s\'est inscrit sur la plateforme.',
        timestamp: new Date(Date.now() - 7200000).toISOString()
      },
      {
        type: 'moderation',
        title: 'Action de modération',
        message: 'Un match a été modifié par un administrateur.',
        timestamp: new Date(Date.now() - 86400000).toISOString()
      }
    ];
  }
}

export default new AdminService();
